import { m } from '@pricedrop/i18n';
import { createFileRoute, useRouterState } from '@tanstack/react-router';
import { SlidersHorizontal } from 'lucide-react';
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { RateLimitPage } from '@/components/rate-limit-page';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { itemsInfiniteQueryOptions, useInfiniteItemsQuery } from '@/hooks/use-infinite-items-query';
import { ApiError, api, fetchApi } from '@/lib/api';
import { useSession } from '@/lib/auth-client';
import { useItemNotificationMap } from '@/lib/notifications';
import { buildCanonicalLink, buildMeta } from '@/lib/seo';
import { ssrForPublicOnly } from '@/lib/ssr-utils';
import { ActiveFilters } from './-components/active-filters';
import { SideFilters } from './-components/filters/side-filters';
import { SideFiltersSkeleton } from './-components/filters/side-filters-skeleton';
import { ItemCard } from './-components/item-card';
import { ItemCardSkeleton } from './-components/item-card-skeleton';
import { NoResults } from './-components/no-results';
import { SortDropdown } from './-components/sort-dropdown';
import { TagsNotificationButton } from './-components/tags-notification-button';
import { parseRawSearchParams, type SortOption, useItemsSearchParams } from './-search-params';
import type { BrandFacet, ItemsSearchParams } from './-types';
import { computeTransitionState } from './-utils';

export const Route = createFileRoute('/items/')({
  ssr: ssrForPublicOnly,
  validateSearch: (search: Record<string, unknown>) => search,
  loaderDeps: ({ search }) => search,
  loader: async ({ deps, context: { queryClient } }) => {
    const parsed = parseRawSearchParams(deps);
    const { page, ...searchParams } = parsed;

    try {
      await queryClient.prefetchInfiniteQuery(itemsInfiniteQueryOptions(searchParams, page));
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        return {
          rateLimited: true as const,
          seoTitle: 'מוצרי KSP - השוואת מחירים | Pricedrop',
          seoDescription: 'עיינו בכל מוצרי KSP עם גרפי היסטוריית מחירים.',
        };
      }
    }

    const query = parsed.query ?? '';
    const brands = parsed.brands.length > 0 ? parsed.brands.join(',') : '';
    const filters = parsed.filters.length > 0 ? parsed.filters.join(',') : '';
    const minPrice = parsed.minPrice != null ? String(parsed.minPrice) : null;
    const maxPrice = parsed.maxPrice != null ? String(parsed.maxPrice) : null;
    const { lowestPricesOnly, backInStockOnly, afterDropOnly } = parsed;

    const brandIds = brands
      ? brands
          .split(',')
          .map(Number)
          .filter((n) => !Number.isNaN(n) && n > 0)
      : [];
    const filterIds = filters
      ? filters
          .split(',')
          .map(Number)
          .filter((n) => !Number.isNaN(n) && n > 0)
      : [];

    let brandNames: string[] = [];
    let filterNames: string[] = [];

    if (brandIds.length > 0 || filterIds.length > 0) {
      try {
        const result = await fetchApi(
          api.api.items.search.post({
            brands: brandIds.length > 0 ? brandIds : undefined,
            filters: filterIds.length > 0 ? filterIds : undefined,
            page: 1,
          }),
        );

        if (brandIds.length > 0 && result?.facetDistribution?.brand) {
          brandNames = (result.facetDistribution.brand as Array<{ id: number; name: string }>)
            .filter((b) => brandIds.includes(b.id) && b.name)
            .map((b) => b.name);
        }

        if (filterIds.length > 0 && result?.facetDistribution?.tagsMerged) {
          filterNames = (
            result.facetDistribution.tagsMerged as Array<{
              valueId: number;
              valueName: string;
            }>
          )
            .filter((t) => filterIds.includes(t.valueId) && t.valueName)
            .map((t) => t.valueName)
            .filter((v, i, arr) => arr.indexOf(v) === i); // deduplicate
        }
      } catch {
        // Fall back to generic text on API error — silent failure
      }
    }

    const hasAnyFlag = lowestPricesOnly || afterDropOnly || backInStockOnly;
    const flagLabel = lowestPricesOnly
      ? 'במחיר הזול ביותר'
      : afterDropOnly
        ? 'אחרי ירידת מחיר'
        : backInStockOnly
          ? 'חזר למלאי'
          : null;

    const priceLabel =
      minPrice != null && maxPrice != null
        ? `₪${minPrice}–₪${maxPrice}`
        : minPrice != null
          ? `מעל ₪${minPrice}`
          : maxPrice != null
            ? `עד ₪${maxPrice}`
            : null;

    const resolvedNames = [...brandNames, ...filterNames].slice(0, hasAnyFlag ? 1 : 2);

    const titleParts: string[] = [];

    if (query) {
      titleParts.push(query);
    } else {
      if (resolvedNames.length > 0) titleParts.push(resolvedNames.join(', '));
      if (flagLabel) titleParts.push(flagLabel);
      if (titleParts.length === 0 && priceLabel) titleParts.push(priceLabel);
      if (titleParts.length === 0 && (brandIds.length > 0 || filterIds.length > 0)) {
        titleParts.push('תוצאות מסוננות');
      }
    }

    const seoTitle =
      titleParts.length > 0
        ? `${titleParts.join(' | ')} | מוצרי KSP | Pricedrop`
        : 'מוצרי KSP - השוואת מחירים | Pricedrop';

    const allNames = [...brandNames, ...filterNames];
    const descElements: string[] = [];

    if (allNames.length > 0) descElements.push(allNames.join(', '));
    if (query) descElements.push(`חיפוש: "${query}"`);
    if (lowestPricesOnly) descElements.push('במחיר הזול ביותר');
    if (afterDropOnly) descElements.push('אחרי ירידת מחיר');
    if (backInStockOnly) descElements.push('חזר למלאי');
    if (priceLabel) descElements.push(priceLabel);

    const seoDescription =
      descElements.length > 0
        ? `מוצרי KSP - ${descElements.join(', ')}. השוו מחירים ומצאו את העסקה הטובה ביותר.`
        : 'עיינו בכל מוצרי KSP עם גרפי היסטוריית מחירים. השוו מחירים, מצאו מבצעים ועקבו אחרי ירידות מחירים.';

    return { seoTitle, seoDescription };
  },
  head: ({ loaderData }) => ({
    meta: buildMeta({
      title: loaderData?.seoTitle ?? 'מוצרי KSP - השוואת מחירים | Pricedrop',
      description:
        loaderData?.seoDescription ??
        'עיינו בכל מוצרי KSP עם גרפי היסטוריית מחירים. השוו מחירים, מצאו מבצעים ועקבו אחרי ירידות מחירים.',
      canonicalUrl: '/items',
    }),
    links: [buildCanonicalLink('/items')],
  }),
  component: ItemsPage,
});

const INITIAL_SKELETON_KEYS = Array.from({ length: 24 }, (_, i) => `skeleton-${i}`);
const NEXT_PAGE_SKELETON_KEYS = Array.from({ length: 24 }, (_, i) => `next-${i}`);
const PREV_PAGE_SKELETON_KEYS = Array.from({ length: 24 }, (_, i) => `prev-${i}`);

function ItemsPage() {
  const loaderData = Route.useLoaderData();
  if (loaderData?.rateLimited) {
    return <RateLimitPage />;
  }
  return <ItemsPageContent />;
}

function ItemsPageContent() {
  const { data: session } = useSession();
  const { data: notificationMap } = useItemNotificationMap(!!session);

  const resolvedPathname = useRouterState({
    select: (s) => s.resolvedLocation?.pathname ?? s.location.pathname,
  });
  const locationPathname = useRouterState({
    select: (s) => s.location.pathname,
  });
  const { isOnItemsPage, isTransitioningAway } = computeTransitionState(
    resolvedPathname,
    locationPathname,
  );

  const [params, setParams] = useItemsSearchParams();
  const { page: rawPage, ...rawSearchParams } = params;

  // Freeze params during transition away to prevent queryKey changes → isLoading → skeleton flash
  const frozenParamsRef = useRef<{
    page: number;
    searchParams: typeof rawSearchParams;
  }>({
    page: rawPage,
    searchParams: rawSearchParams,
  });
  if (!isTransitioningAway) {
    frozenParamsRef.current = { page: rawPage, searchParams: rawSearchParams };
  }
  const page = frozenParamsRef.current.page;
  const searchParams = frozenParamsRef.current.searchParams;

  const {
    data,
    isLoading,
    isPlaceholderData,
    error,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useInfiniteItemsQuery(searchParams, page, { enabled: isOnItemsPage });

  const showSkeleton = (isLoading || isPlaceholderData) && !error;

  const handleParamsChange = (updates: Partial<ItemsSearchParams>) => {
    scrollPositionRef.current = null;
    setVisiblePage(updates.page ?? 1);
    setTopObserverEnabled(false);
    window.scrollTo(0, 0);
    setParams({ ...updates, page: updates.page ?? 1 });
  };

  const handleClearAll = () => {
    handleParamsChange({
      page: 1,
      sort: params.sort,
      afterDropOnly: false,
      backInStockOnly: false,
      lowestPricesOnly: false,
      includeOutOfStock: false,
      minPrice: null,
      maxPrice: null,
      brands: [],
      filters: [],
      query: null,
    });
  };

  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const hasActiveFilters =
    (params.query?.length ?? 0) > 0 ||
    (params.brands?.length ?? 0) > 0 ||
    (params.filters?.length ?? 0) > 0 ||
    params.afterDropOnly ||
    params.backInStockOnly ||
    params.lowestPricesOnly ||
    params.includeOutOfStock ||
    params.minPrice != null ||
    params.maxPrice != null;

  const handleSortChange = (sort: SortOption) => {
    handleParamsChange({ sort, page: 1 });
  };

  const allItems = useMemo(() => data?.pages?.flatMap((p) => p.items) ?? [], [data?.pages]);
  const startingPage = useMemo(() => data?.pages?.[0]?.page ?? 1, [data?.pages]);
  const currentPageOffset = (page - startingPage) * 24;
  const firstPage = data?.pages?.[0];
  const searchParamsKey = useMemo(() => JSON.stringify(searchParams), [searchParams]);

  const bottomObserverRef = useRef<HTMLDivElement>(null);
  const topObserverRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<{
    scrollY: number;
    scrollHeight: number;
  } | null>(null);
  const isFirstMountRef = useRef(true);
  const hasFetchedInitialPreviousRef = useRef(false);
  const [topObserverEnabled, setTopObserverEnabled] = useState(false);
  const [visiblePage, setVisiblePage] = useState(page);
  const pageMarkersRef = useRef<Map<number, HTMLDivElement>>(new Map());
  const visiblePageRef = useRef(visiblePage);
  visiblePageRef.current = visiblePage;

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only react to external page changes — including visiblePage/isTransitioningAway would cause update loop
  useEffect(() => {
    if (isTransitioningAway) return;
    if (params.page !== visiblePage) {
      setVisiblePage(params.page);
    }
  }, [params.page]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: searchParamsKey is a stable serialization — scrollPositionRef/isFirstMountRef are refs
  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      return;
    }
    if (isTransitioningAway) return;
    scrollPositionRef.current = null;
    setTopObserverEnabled(false);
    setVisiblePage(params.page);
    hasFetchedInitialPreviousRef.current = false;
    window.scrollTo(0, 0);
  }, [searchParamsKey]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: isPlaceholderData/isTransitioningAway guard against stale state during transitions
  useEffect(() => {
    if (isTransitioningAway) return;
    if (isPlaceholderData) return;
    if (visiblePage !== params.page) {
      setParams({ page: visiblePage }, { scroll: false, history: 'replace' });
    }
  }, [visiblePage, params.page, setParams, isPlaceholderData]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: data?.pages?.length triggers re-observation when new pages load — pageMarkersRef/visiblePageRef are refs
  useEffect(() => {
    if (isTransitioningAway) return;

    const markers = Array.from(pageMarkersRef.current.entries());
    if (markers.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const pageNum = Number(entry.target.getAttribute('data-page'));
            if (!Number.isNaN(pageNum) && pageNum !== visiblePageRef.current) {
              setVisiblePage(pageNum);
            }
          }
        }
      },
      { root: null, rootMargin: '-20% 0px', threshold: 0.5 },
    );

    for (const [, element] of markers) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, [data?.pages?.length]);

  useEffect(() => {
    const target = bottomObserverRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          hasNextPage &&
          !isFetchingNextPage &&
          !isFetchingPreviousPage &&
          !error
        ) {
          fetchNextPage();
        }
      },
      { rootMargin: '250px', threshold: 0 },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isFetchingNextPage, isFetchingPreviousPage, fetchNextPage, error]);

  // Top IntersectionObserver — load previous pages when scrolling up
  useEffect(() => {
    if (!topObserverEnabled) return;

    const target = topObserverRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          hasPreviousPage &&
          !isFetchingPreviousPage &&
          !isFetchingNextPage
        ) {
          fetchPreviousPage();
        }
      },
      { rootMargin: '250px', threshold: 0 },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [
    topObserverEnabled,
    hasPreviousPage,
    isFetchingPreviousPage,
    isFetchingNextPage,
    fetchPreviousPage,
  ]);

  useLayoutEffect(() => {
    if (!isFetchingPreviousPage && scrollPositionRef.current) {
      const saved = scrollPositionRef.current;
      const newScrollHeight = document.documentElement.scrollHeight;
      const heightDiff = newScrollHeight - saved.scrollHeight;
      window.scrollTo(0, saved.scrollY + heightDiff);
      scrollPositionRef.current = null;
    }
  }, [isFetchingPreviousPage]);

  useEffect(() => {
    if (
      page > 1 &&
      allItems.length > 0 &&
      hasPreviousPage &&
      !isFetchingPreviousPage &&
      !hasFetchedInitialPreviousRef.current
    ) {
      hasFetchedInitialPreviousRef.current = true;
      scrollPositionRef.current = {
        scrollY: window.scrollY,
        scrollHeight: document.documentElement.scrollHeight,
      };
      fetchPreviousPage();
    }
  }, [page, allItems.length, hasPreviousPage, isFetchingPreviousPage, fetchPreviousPage]);

  // Delayed top observer activation — only enable after user scrolls up
  useEffect(() => {
    if (topObserverEnabled) return;

    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY < lastScrollY) {
        setTopObserverEnabled(true);
      }
      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [topObserverEnabled]);

  useEffect(() => {
    if (!error) return;
    if (error instanceof ApiError && error.status === 429) {
      toast.error(`🚦 ${m.rateLimit_toastTitle()}`, {
        id: 'items-rate-limit-toast',
        description: session ? m.rateLimit_toastDescAuth() : m.rateLimit_toastDescGuest(),
        duration: 6000,
      });
    } else {
      toast.error(m.items_loadError());
    }
  }, [error, session]);

  const resolvedTagNames = useMemo(() => {
    if (!params.filters.length || !firstPage?.facetDistribution?.tagsMerged) return [];
    const tagLookup = new Map(
      firstPage.facetDistribution.tagsMerged.map((t) => [t.valueId, t.valueName]),
    );
    return params.filters.map((id) => tagLookup.get(id) ?? `#${id}`);
  }, [params.filters, firstPage?.facetDistribution?.tagsMerged]);

  const resolvedBrands = useMemo(() => {
    if (params.brands.length === 0 || !firstPage?.facetDistribution?.brand) return null;
    const brandFacets = params.brands
      .map((id) => firstPage.facetDistribution?.brand?.find((b) => b.id === id))
      .filter((b): b is BrandFacet => b !== undefined);
    return brandFacets.length > 0
      ? {
          ids: brandFacets.map((b) => b.id),
          names: brandFacets.map((b) => b.name),
        }
      : null;
  }, [params.brands, firstPage?.facetDistribution?.brand]);

  return (
    <div>
      <div className="flex items-center gap-2 px-4 pt-4">
        <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="h-8 gap-1.5 px-2 sm:px-3 xl:hidden">
              <SlidersHorizontal className="h-3.5 w-3.5" />
              <span className="hidden text-xs sm:inline">סינון</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-80 overflow-y-auto p-0">
            <SheetHeader className="px-4 pt-4">
              <SheetTitle>סינון מוצרים</SheetTitle>
            </SheetHeader>
            {!showSkeleton &&
              firstPage &&
              (() => {
                const priceStats = firstPage.facetStats?.price ?? { min: 0, max: 0 };
                return (
                  <SideFilters
                    brands={firstPage.facetDistribution?.brand ?? []}
                    tags={firstPage.facetDistribution?.tagsMerged ?? []}
                    priceStats={priceStats as { min: number; max: number }}
                    priceHistogram={firstPage.facetDistribution?.price ?? []}
                    currentParams={params}
                    onParamsChange={(updates) => {
                      handleParamsChange(updates);
                      setFilterSheetOpen(false);
                    }}
                  />
                );
              })()}
          </SheetContent>
        </Sheet>
        <div className="ms-auto flex items-center gap-2">
          {(params.filters.length > 0 || params.brands.length > 0) && firstPage && (
            <TagsNotificationButton
              tags={params.filters}
              tagNames={resolvedTagNames}
              brandIds={resolvedBrands?.ids}
              brandNames={resolvedBrands?.names}
            />
          )}
          <SortDropdown value={params.sort} onChange={handleSortChange} />
        </div>
      </div>
      <div className="flex">
        <div className="sticky top-14 hidden max-h-[calc(100vh-3.5rem)] w-64 shrink-0 self-start overflow-y-auto overscroll-contain xl:block">
          {isLoading && !firstPage ? (
            <SideFiltersSkeleton />
          ) : firstPage ? (
            (() => {
              const priceStats = firstPage.facetStats?.price ?? { min: 0, max: 0 };
              return (
                <SideFilters
                  brands={firstPage.facetDistribution?.brand ?? []}
                  tags={firstPage.facetDistribution?.tagsMerged ?? []}
                  priceStats={priceStats as { min: number; max: number }}
                  priceHistogram={firstPage.facetDistribution?.price ?? []}
                  currentParams={params}
                  onParamsChange={handleParamsChange}
                />
              );
            })()
          ) : null}
        </div>

        <div className="flex-1">
          <ActiveFilters
            currentParams={params}
            brands={firstPage?.facetDistribution?.brand ?? []}
            tags={firstPage?.facetDistribution?.tagsMerged ?? []}
            onParamsChange={handleParamsChange}
            onClearAll={handleClearAll}
          />
          {showSkeleton ? (
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4">
              {INITIAL_SKELETON_KEYS.map((id) => (
                <ItemCardSkeleton key={id} />
              ))}
            </div>
          ) : error instanceof ApiError && error.status === 429 && allItems.length === 0 ? (
            <RateLimitPage />
          ) : allItems.length === 0 ? (
            <NoResults hasActiveFilters={hasActiveFilters} onClearFilters={handleClearAll} />
          ) : (
            <>
              {hasPreviousPage && <div ref={topObserverRef} className="h-10" />}

              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4">
                {isFetchingPreviousPage &&
                  !scrollPositionRef.current &&
                  PREV_PAGE_SKELETON_KEYS.map((id) => <ItemCardSkeleton key={id} />)}

                {allItems.map((item, index) => {
                  const pageOffset = Math.floor(index / 24);
                  const actualPageNumber = startingPage + pageOffset;

                  return (
                    <Fragment key={item.uin}>
                      {index % 24 === 12 && (
                        <div
                          ref={(el) => {
                            if (el) pageMarkersRef.current.set(actualPageNumber, el);
                            else pageMarkersRef.current.delete(actualPageNumber);
                          }}
                          data-page={actualPageNumber}
                          className="col-span-full h-0 pointer-events-none"
                          aria-hidden="true"
                        />
                      )}
                      <ItemCard
                        name={item.name}
                        uin={item.uin}
                        uinsql={item.uinsql}
                        brandName={item.brandName}
                        price={item.price}
                        highestPrice={item.highestPrice}
                        lowestPrice={item.lowestPrice}
                        meanPrice={item.meanPrice}
                        previousPrice={item.previousPrice}
                        lazyLoad={index < currentPageOffset || index >= currentPageOffset + 8}
                        notification={notificationMap?.get(item.uin) ?? null}
                      />
                    </Fragment>
                  );
                })}

                {isFetchingNextPage &&
                  NEXT_PAGE_SKELETON_KEYS.map((id) => <ItemCardSkeleton key={id} />)}
              </div>

              <div ref={bottomObserverRef} className="h-10" />

              {!hasNextPage && allItems.length > 0 && (
                <div className="p-8 text-center text-muted-foreground">הגעת לסוף התוצאות</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
